// ./src/components/features/Artists/EventArtistsFilterBar/EventArtistsFilterBar.tsx
//
// Filter controls for the event artist bookings list: status (visible/hidden),
// booking status (multi), billing (multi). Controlled — the page maps it to an
// EventArtistFilter. Mirrors EventPartnersFilterBar.
//
// ONE row on a desktop (owner, 2026-10-16: "can we try to fit the date selector in the filters row? We can make other
// filters narrower … the status filter can become dropdown"): an optional `leading` slot hosts the page's date picker
// (as TasksFilterBar hosts its view toggle), the status is a small dropdown, and the two multi-value facets are the
// compact `CheckableMultiSelect` ("N selected", one line — never Mantine's pill-growing MultiSelect). In the phone's
// filter drawer (`vertical`) every control is full width.

import React from "react";
import { Group, Button, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  ARTIST_BOOKING_STATUS_VALUES,
  ARTIST_BOOKING_STATUS_LABELS,
  ARTIST_BILLING_VALUES,
  ARTIST_BILLING_LABELS,
} from "@/utils/api";
import { AppIcon, CheckableMultiSelect, SelectField } from "@/components/generic";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";

export type ArtistStatusFilter = "visible" | "hidden";

export interface EventArtistsFilterValue {
  status: ArtistStatusFilter | null;
  bookingStatuses: string[];
  billings: string[];
}

export interface EventArtistsFilterBarProps {
  value: EventArtistsFilterValue;
  onChange: (value: EventArtistsFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
  vertical?: boolean;
  /** Optional control rendered at the start of the row (the page's date picker). */
  leading?: React.ReactNode;
}

export const EMPTY_EVENT_ARTISTS_FILTER: EventArtistsFilterValue = {
  status: null,
  bookingStatuses: [],
  billings: [],
};

export const isEventArtistsFilterActive = (v: EventArtistsFilterValue): boolean =>
  Boolean(v.status) || v.bookingStatuses.length > 0 || v.billings.length > 0;

const STATUS_STYLE: React.CSSProperties = { width: 140 };
const BOOKING_STYLE: React.CSSProperties = { width: 190 };
const BILLING_STYLE: React.CSSProperties = { width: 170 };

const ANY = "any";

const EventArtistsFilterBarComponent: React.FC<EventArtistsFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
  vertical = false,
  leading,
}) => {
  const { t } = useTranslation(["artists", "common"]);

  const statusOptions = [
    { value: ANY, label: t("artists:filters.status.any", { defaultValue: "Any" }) },
    { value: "visible", label: t("artists:filters.status.visible", { defaultValue: "Shown" }) },
    { value: "hidden", label: t("artists:filters.status.hidden", { defaultValue: "Hidden" }) },
  ];

  const bookingOptions = ARTIST_BOOKING_STATUS_VALUES.map((v) => ({
    value: v,
    label: t(`artists:bookingStatus.${v}`, { defaultValue: ARTIST_BOOKING_STATUS_LABELS[v] }),
  }));

  const billingOptions = ARTIST_BILLING_VALUES.map((v) => ({
    value: v,
    label: t(`artists:billing.${v}`, { defaultValue: ARTIST_BILLING_LABELS[v] }),
  }));

  const active = isEventArtistsFilterActive(value);

  return (
    <Group gap="sm" align="flex-end" wrap="wrap" style={vertical ? { flexDirection: "column", alignItems: "stretch" } : undefined}>
      {leading}

      <Box style={vertical ? undefined : STATUS_STYLE}>
        <SelectField
          name="status"
          size="md"
          label={t("artists:filters.status.label", { defaultValue: "On the lineup" })}
          data={statusOptions}
          value={value.status ?? ANY}
          onChange={(_n, next) => onChange({ ...value, status: !next || next === ANY ? null : (next as ArtistStatusFilter) })}
          allowDeselect={false}
          disabled={disabled}
          // In the phone's filter drawer the dropdown must portal, or it resizes the sheet and flickers.
          comboboxProps={vertical ? DRAWER_COMBOBOX_PROPS : { withinPortal: true }}
        />
      </Box>

      <Box style={vertical ? undefined : BOOKING_STYLE}>
        <CheckableMultiSelect
          label={t("artists:filters.booking.label", { defaultValue: "Booking status" })}
          placeholder={t("artists:filters.booking.placeholder", { defaultValue: "Any status" })}
          data={bookingOptions}
          value={value.bookingStatuses}
          onChange={(next) => onChange({ ...value, bookingStatuses: next })}
          disabled={disabled}
          size="md"
        />
      </Box>

      <Box style={vertical ? undefined : BILLING_STYLE}>
        <CheckableMultiSelect
          label={t("artists:filters.billing.label", { defaultValue: "Billing" })}
          placeholder={t("artists:filters.billing.placeholder", { defaultValue: "Any billing" })}
          data={billingOptions}
          value={value.billings}
          onChange={(next) => onChange({ ...value, billings: next })}
          disabled={disabled}
          size="md"
        />
      </Box>

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_EVENT_ARTISTS_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const EventArtistsFilterBar = React.memo(EventArtistsFilterBarComponent);
